import Helper from '../../../models/Helper';

const URI = '/courses/:id';

/**
 * @desc Helper for courses.
 */
class CoursesHelper extends Helper {
  constructor(api) {
    super(api, URI);
  }

  /**
   * @desc Get a specific course from its id.
   * @param {Number} courseId Id of the course to get.
   * @return {Promise<Course>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.get(courseId);
   */
  get(courseId: number) {
    return super.get(courseId);
  }

  /**
   * @desc Search for some courses with given parameters.
   * @param {CoursesFilterOptions} [filters={}] Filters to use to find courses.
   * @return {Promise<Array<Course>>} The promise handling the request.
   * @example
   * const filters = { category: 'developpement-web' };
   * const response = await myClient.helpers.learningContent.courses.find(filters);
   * @example
   * const filters = { q: 'php' };
   * const response = await myClient.helpers.learningContent.courses.find(filters);
   * @example
   * const filters = { category: 'developpement-web', q: 'php' };
   * const response = await myClient.helpers.learningContent.courses.find(filters);
   * @example
   * const filters = { start: 0, end: 10 };
   * const response = await myClient.helpers.learningContent.courses.find(filters);
   * @example
   * const filters = { category: 'developpement-web', start: 0, end: 10 };
   * const response = await myClient.helpers.learningContent.courses.find(filters);
   */
  find(filters = {}) {
    return super.find(filters);
  }

  /**
   * @desc Get table of content of specific course.
   * @param {Number} courseId Id of the course to get table of content.
   * @return {Promise<TocItem>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.getTableOfContent(courseId);
   */
  getTableOfContent(courseId: number) {
    const request = super.get(courseId, false);
    request.url += '/table-of-content';

    return request.execute();
  }

  /**
   * @desc Get content of specific course.
   * @param {Number} courseId Id of the course to get content.
   * @return {Promise<String>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.getContent(courseId);
   */
  getContent(
    courseId: number,
    queryParams?: {
      status?: string;
    }
  ) {
    const request = super.get(courseId, false);

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    request.url += '/content';
    request.setHeader('Accept', 'text/html');
    request.setHeader('Content-Type');

    return request.execute();
  }

  /**
   * @desc Get introduction of specific course.
   * @param {Number} courseId Id of the course to get introduction.
   * @param {Boolean} [shouldReturnTextPlain=false] If `false`, introduction will be returned as
   * HTML content. If `true`, plain text will be returned.
   * @return {Promise<String>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.getIntroduction(courseId);
   * @example
   * const courseId = 1234;
   * const shouldReturnTextPlain = true;
   * const response = await myClient.helpers.learningContent
   *                    .courses.getIntroduction(courseId, shouldReturnTextPlain);
   */
  getIntroduction(
    courseId: number,
    shouldReturnTextPlain = false,
    queryParams?: {
      status?: string;
    }
  ) {
    const request = super.get(courseId, false);

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    request.url += '/introduction';
    request.setHeader('Accept', `text/${shouldReturnTextPlain ? 'plain' : 'html'}`);
    request.setHeader('Content-Type');

    return request.execute();
  }

  /**
   * @desc Get learning outcomes of specific course.
   * @param {Number} courseId Id of the course to get content.
   * @return {Promise<Array<LearningOutcome>>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.getLearningOutcomes(courseId);
   */
  getLearningOutcomes(courseId: number) {
    const request = super.get(courseId, false);
    request.url += '/learning-outcomes';

    return request.execute();
  }

  /**
   * @desc Get the paths that are recommended for a specific course.
   * @param {Number} courseId Id of the course to get content.
   * @return {Promise<Array<Path>>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningContent.courses.getRecommendedPaths(courseId);
   */
  getRecommendedPaths(courseId) {
    const request = super.get(courseId, false);
    request.url += '/recommended-paths';

    return request.execute();
  }
}

export default CoursesHelper;
